import dayjs, {Dayjs} from "dayjs";

type SortableValue = string | string[] | number | boolean | null | undefined;

const useProgramConsumerSort = () => {
    /**
     * Sort two values according to their type and content.
     */
    const sort = (a: SortableValue, b: SortableValue) => {
        const compares = [
            compareNullish,
            compareArrays,
            compareBooleans,
            compareNumbers
        ];

        for (const compare of compares) {
            const result = compare(a, b);
            if (result !== null) {
                return result;
            }
        }

        return compareStrings(String(a), String(b));
    };

    /**
     * Compare nullish values, placing them at the end.
     * Returns null if both values are non-nullish.
     */
    const compareNullish = (a: SortableValue, b: SortableValue): number | null => {
        const aIsNullish = a === null || a === undefined || a === '';
        const bIsNullish = b === null || b === undefined || b === '';

        if (aIsNullish && bIsNullish) return 0;
        if (aIsNullish) return 1;
        if (bIsNullish) return -1;

        return null;
    };

    /**
     * Compare array values item by item, then by their length.
     * Returns null if both values are non-arrays.
     */
    const compareArrays = (a: SortableValue, b: SortableValue): number | null => {
        const aIsArray = Array.isArray(a);
        const bIsArray = Array.isArray(b);

        if (aIsArray && bIsArray) {
            for (let index = 0; index < Math.min(a.length, b.length); index++) {
                const result = sort(a[index], b[index]);
                if (result !== 0) return result;
            }

            return a.length - b.length;
        }

        if (aIsArray) return 1;
        if (bIsArray) return -1;

        return null;
    };

    /**
     * Compare boolean values, placing true before false.
     * Returns null if both values are non-booleans.
     */
    const compareBooleans = (a: SortableValue, b: SortableValue): number | null => {
        const aIsBoolean = typeof a === 'boolean';
        const bIsBoolean = typeof b === 'boolean';

        if (aIsBoolean && bIsBoolean) {
            return (a === b) ? 0 : a ? -1 : 1;
        }

        if (aIsBoolean) return 1;
        if (bIsBoolean) return -1;

        return null;
    };

    /**
     * Compare number values.
     * Returns null if both values are non-numbers.
     */
    const compareNumbers = (a: SortableValue, b: SortableValue): number | null => {
        if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
        }

        return null;
    };

    /**
     * Compare string values, handling dates and locale-aware comparison.
     */
    const compareStrings = (a: string, b: string) => {
        const dateResult = compareDates(a, b);
        if (dateResult) return dateResult;

        return a.localeCompare(b, 'fr', {numeric: true, sensitivity: 'base'});
    };

    /**
     * Parse a date string into a Dayjs object.
     */
    const parseExactDate = (date: string): Dayjs | null => {
        const DATE_FORMATS = [
            'DD/MM/YYYY',
            'DD-MM-YYYY',
            'DD/MM/YY',
            'DD-MM-YY',
            'YYYY-MM-DD',
            'YYYY/MM/DD',
            'YYYY-MM-DD HH:mm:ss',
        ];

        for (const format of DATE_FORMATS) {
            const parsed = dayjs(date, format, true);
            if (parsed.isValid()) {
                return parsed;
            }
        }

        return null;
    };

    /**
     * Parse a string that is a date, or that contains one (e.g. "2 doses de Shingrix (04/06/2025)").
     */
    const parseDate = (value: string): Dayjs | null => {
        const exact = parseExactDate(value);
        if (exact) return exact;

        const embedded = value.match(/\b(?:\d{4}[/-]\d{2}[/-]\d{2}|\d{2}[/-]\d{2}[/-]\d{2}(?:\d{2})?)\b/);

        return embedded ? parseExactDate(embedded[0]) : null;
    };

    /**
     * Compare two date strings.
     * Returns null if both strings are not valid dates.
     */
    const compareDates = (a: string, b: string) => {
        const dateA = parseDate(a);
        const dateB = parseDate(b);

        if (dateA && dateB) {
            return dateA.valueOf() - dateB.valueOf();
        }

        return null;
    };

    return {sort};
};

export default useProgramConsumerSort;
